// Aba "Configuração" — gerencia configs injetadas no system prompt do Dwight.
// Cada item é um par chave/valor + tipo (texto, multilinha, número, json).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Alert, Tag, Input, Typography, Space, Flex, App as AntApp, Empty,
} from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import type { ConfigItem } from './types';

const { Text } = Typography;

export default function ConfigTab() {
  const { message, modal } = AntApp.useApp();
  const [items, setItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [edicoes, setEdicoes] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<string | null>(null);

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/config`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setItems(Array.isArray(data) ? data : []);
      setEdicoes({});
    } finally { setLoading(false); }
  }
  async function salvar(chave: string) {
    const valor = edicoes[chave];
    if (valor === undefined) return;
    setSalvando(chave);
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/agentes/config/${chave}`, { valor },
        { headers: { Authorization: token } });
      message.success('Salvo.');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(null); }
  }
  function resetar(chave: string) {
    modal.confirm({
      title: 'Restaurar valor padrão?', okText: 'Restaurar', cancelText: 'Cancelar',
      onOk: async () => {
        setSalvando(chave);
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/agentes/config/${chave}/reset`, {},
            { headers: { Authorization: token } });
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setSalvando(null); }
      },
    });
  }

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 14 }}
        message={
          <Text style={{ fontSize: 12 }}>
            💡 Configurações injetadas no <strong>system prompt</strong> do Dwight.
            Mudanças entram em vigor <strong>imediatamente</strong>. "Restaurar padrão" desfaz.
          </Text>
        } />
      {loading ? <Card loading /> : items.length === 0 ? (
        <Empty description="Sem configurações. (Bootstrap deve criar — recarregue.)" />
      ) : (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          {items.map(c => {
            const valorAtual = edicoes[c.chave] !== undefined ? edicoes[c.chave] : c.valor;
            const editado = edicoes[c.chave] !== undefined && edicoes[c.chave] !== c.valor;
            return (
              <Card key={c.chave} size="small"
                style={{ borderColor: editado ? '#B07500' : undefined, borderWidth: editado ? 1 : undefined }}>
                <Flex align="baseline" gap={8} style={{ marginBottom: 4 }} wrap="wrap">
                  <Text code style={{ color: '#020D7F', fontWeight: 600 }}>{c.chave}</Text>
                  <Tag color="default">{c.tipo}</Tag>
                  {editado && <Text style={{ fontSize: 10, color: '#B07500' }}>● editado</Text>}
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>
                    {c.atualizado_por ? `por ${c.atualizado_por.split('@')[0]} · ` : ''}{fmtDateTime(c.atualizado_em)}
                  </Text>
                </Flex>
                {c.descricao && (
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>{c.descricao}</Text>
                )}
                {c.tipo === 'numero' ? (
                  <Input type="number" step="0.01" value={valorAtual} style={{ width: 200 }}
                    onChange={(e) => setEdicoes(prev => ({ ...prev, [c.chave]: e.target.value }))} />
                ) : c.tipo === 'multilinha' ? (
                  <Input.TextArea value={valorAtual} autoSize={{ minRows: 5, maxRows: 30 }}
                    onChange={(e) => setEdicoes(prev => ({ ...prev, [c.chave]: e.target.value }))} />
                ) : (
                  <Input value={valorAtual}
                    onChange={(e) => setEdicoes(prev => ({ ...prev, [c.chave]: e.target.value }))} />
                )}
                <Flex gap={6} style={{ marginTop: 8 }}>
                  <Button size="small" type="primary" disabled={!editado} loading={salvando === c.chave}
                    onClick={() => salvar(c.chave)}>Salvar</Button>
                  {editado && (
                    <Button size="small" onClick={() => setEdicoes(prev => {
                      const n = { ...prev }; delete n[c.chave]; return n;
                    })}>Desfazer edição</Button>
                  )}
                  <Button size="small" style={{ marginLeft: 'auto' }} onClick={() => resetar(c.chave)}>
                    Restaurar padrão
                  </Button>
                </Flex>
              </Card>
            );
          })}
        </Space>
      )}
    </div>
  );
}
